if (!customElements.get('purchase-image-switcher')) {
  customElements.define('purchase-image-switcher', class PurchaseImageSwitcher extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.timer = null;
      this.isPointerInside = false;
      this.isFocusInside = false;
      this.isTouchDragging = false;
      this.viewportSyncTarget = null;
      this.viewportSyncTimer = null;
      this.mobileMediaQuery = window.matchMedia('(max-width: 749px)');
      this.wasMobileViewport = this.mobileMediaQuery.matches;
      this.handleResize = this.handleResize.bind(this);
      this.handleTabsScroll = this.updateIndicator.bind(this);
      this.handleViewportScroll = this.handleViewportScroll.bind(this);
      this.handleMouseEnter = this.pauseAutoplay.bind(this);
      this.handleMouseLeave = this.resumeAutoplay.bind(this);
      this.handleFocusIn = this.pauseAutoplay.bind(this);
      this.handleFocusOut = this.resumeAutoplay.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-switcher-viewport]');
      this.panels = Array.from(this.querySelectorAll('[data-switcher-panel]'));
      this.triggers = Array.from(this.querySelectorAll('[data-switcher-trigger]'));
      this.captions = Array.from(this.querySelectorAll('[data-switcher-caption]'));
      this.links = Array.from(this.querySelectorAll('[data-switcher-link]'));
      this.indicator = this.querySelector('[data-switcher-indicator]');
      this.tabs = this.querySelector('.purchase-image-switcher__tabs');
      this.autoplayEnabled = this.dataset.autoplay === 'true' && !(window.Shopify && window.Shopify.designMode);
      this.interval = Number(this.dataset.interval) || 5000;

      this.handleTriggerClick = this.handleTriggerClick.bind(this);
      this.handleTriggerKeydown = this.handleTriggerKeydown.bind(this);

      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', this.handleTriggerClick);
        trigger.addEventListener('keydown', this.handleTriggerKeydown);
      });

      if (this.tabs) {
        this.tabs.addEventListener('scroll', this.handleTabsScroll, { passive: true });
      }

      if (this.viewport) {
        this.viewport.addEventListener('scroll', this.handleViewportScroll, { passive: true });
        this.viewport.addEventListener('touchstart', this.handleTouchStart, { passive: true });
        this.viewport.addEventListener('touchend', this.handleTouchEnd, { passive: true });
        this.viewport.addEventListener('touchcancel', this.handleTouchEnd, { passive: true });
      }

      this.addEventListener('mouseenter', this.handleMouseEnter);
      this.addEventListener('mouseleave', this.handleMouseLeave);
      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);
      window.addEventListener('resize', this.handleResize);

      this.setActive(this.getInitialIndex(), { focus: false, force: true, instant: true });
      requestAnimationFrame(() => {
        this.syncViewportToCurrent({ instant: true });
        this.updateIndicator();
      });
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      window.clearTimeout(this.viewportSyncTimer);

      this.triggers.forEach((trigger) => {
        trigger.removeEventListener('click', this.handleTriggerClick);
        trigger.removeEventListener('keydown', this.handleTriggerKeydown);
      });

      if (this.tabs) {
        this.tabs.removeEventListener('scroll', this.handleTabsScroll);
      }

      if (this.viewport) {
        this.viewport.removeEventListener('scroll', this.handleViewportScroll);
        this.viewport.removeEventListener('touchstart', this.handleTouchStart);
        this.viewport.removeEventListener('touchend', this.handleTouchEnd);
        this.viewport.removeEventListener('touchcancel', this.handleTouchEnd);
      }

      this.removeEventListener('mouseenter', this.handleMouseEnter);
      this.removeEventListener('mouseleave', this.handleMouseLeave);
      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);
      window.removeEventListener('resize', this.handleResize);
    }

    getInitialIndex() {
      const activeTrigger = this.triggers.find((trigger) => trigger.classList.contains('is-active'));
      const index = activeTrigger ? Number(activeTrigger.dataset.index) : 0;
      return Number.isNaN(index) ? 0 : index;
    }

    isMobileViewport() {
      return this.mobileMediaQuery.matches;
    }

    getClosestViewportIndex() {
      if (!this.viewport) {
        return this.currentIndex;
      }

      const viewportWidth = this.viewport.clientWidth || 1;
      const rawIndex = Math.round(this.viewport.scrollLeft / viewportWidth);
      return Math.max(0, Math.min(this.panels.length - 1, rawIndex));
    }

    getViewportOffset(index) {
      if (!this.viewport) {
        return 0;
      }

      return this.viewport.clientWidth * index;
    }

    scrollTabsToTrigger(trigger, instant = false) {
      if (!this.tabs || !trigger) {
        return;
      }

      const targetLeft = trigger.offsetLeft - ((this.tabs.clientWidth - trigger.clientWidth) / 2);
      const maxScrollLeft = Math.max(0, this.tabs.scrollWidth - this.tabs.clientWidth);
      const nextScrollLeft = Math.min(Math.max(0, targetLeft), maxScrollLeft);

      this.tabs.scrollTo({
        left: nextScrollLeft,
        behavior: instant ? 'auto' : 'smooth',
      });
    }

    handleResize() {
      const isMobileViewport = this.isMobileViewport();

      this.updateIndicator();

      if (this.viewport && (isMobileViewport || isMobileViewport !== this.wasMobileViewport)) {
        this.syncViewportToCurrent({ instant: true });
      }

      this.wasMobileViewport = isMobileViewport;
    }

    handleViewportScroll() {
      if (!this.viewport || !this.isMobileViewport() || this.panels.length < 2) {
        return;
      }

      if (this.viewportSyncTarget !== null) {
        if (Math.abs(this.viewport.scrollLeft - this.viewportSyncTarget) > 2) {
          return;
        }

        this.viewportSyncTarget = null;
      }

      const nextIndex = this.getClosestViewportIndex();

      if (nextIndex !== this.currentIndex) {
        this.applyActiveState(nextIndex, { focus: false, instant: true });
      }
    }

    handleTouchStart() {
      this.isTouchDragging = true;
      this.stopAutoplay();
    }

    handleTouchEnd() {
      this.isTouchDragging = false;
      this.startAutoplay();
    }

    handleTriggerClick(event) {
      const index = Number(event.currentTarget.dataset.index);
      this.setActive(index, { focus: false });
      this.restartAutoplay();
    }

    handleTriggerKeydown(event) {
      const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];

      if (!keys.includes(event.key)) {
        return;
      }

      event.preventDefault();

      if (event.key === 'Home') {
        this.setActive(0, { focus: true });
        return;
      }

      if (event.key === 'End') {
        this.setActive(this.triggers.length - 1, { focus: true });
        return;
      }

      const direction = event.key === 'ArrowRight' ? 1 : -1;
      const nextIndex = (this.currentIndex + direction + this.triggers.length) % this.triggers.length;
      this.setActive(nextIndex, { focus: true });
    }

    setActive(index, options = {}) {
      if (!this.panels.length || index < 0 || index >= this.panels.length) {
        return;
      }

      if (index === this.currentIndex && !options.force) {
        return;
      }

      this.applyActiveState(index, options);

      if (options.updateViewport !== false) {
        this.syncViewportToCurrent({ instant: Boolean(options.instant) });
      }
    }

    applyActiveState(index, options = {}) {
      if (!this.panels.length || index < 0 || index >= this.panels.length) {
        return;
      }

      this.currentIndex = index;
      this.updateActiveColors(index);

      this.panels.forEach((panel, panelIndex) => {
        const isActive = panelIndex === index;
        panel.classList.toggle('is-active', isActive);
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      this.triggers.forEach((trigger, triggerIndex) => {
        const isActive = triggerIndex === index;
        trigger.classList.toggle('is-active', isActive);
        trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
        trigger.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      this.captions.forEach((caption, captionIndex) => {
        const isActive = captionIndex === index;
        caption.classList.toggle('is-active', isActive);
        caption.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      this.links.forEach((link, linkIndex) => {
        if (link.tagName !== 'A') {
          return;
        }

        link.setAttribute('tabindex', linkIndex === index ? '0' : '-1');
      });

      if (options.focus && this.triggers[index]) {
        this.triggers[index].focus({ preventScroll: true });
      }

      if (this.tabs && this.triggers[index]) {
        this.scrollTabsToTrigger(this.triggers[index], options.instant);
      }

      this.updateIndicator();
    }

    updateActiveColors(index) {
      const activeTrigger = this.triggers[index];

      if (!activeTrigger) {
        return;
      }

      const colorMap = {
        '--purchase-switcher-text': activeTrigger.dataset.textColor,
        '--purchase-switcher-muted': activeTrigger.dataset.mutedColor,
        '--purchase-switcher-indicator': activeTrigger.dataset.indicatorColor,
        '--purchase-switcher-line': activeTrigger.dataset.lineColor,
      };

      Object.entries(colorMap).forEach(([property, value]) => {
        if (value) {
          this.style.setProperty(property, value);
        }
      });
    }

    syncViewportToCurrent({ instant = false } = {}) {
      if (!this.viewport || !this.isMobileViewport() || this.panels.length < 2) {
        return;
      }

      const nextOffset = this.getViewportOffset(this.currentIndex);

      if (Math.abs(this.viewport.scrollLeft - nextOffset) <= 2) {
        this.viewportSyncTarget = null;
        return;
      }

      window.clearTimeout(this.viewportSyncTimer);
      this.viewportSyncTarget = instant ? null : nextOffset;

      if (!instant) {
        this.viewportSyncTimer = window.setTimeout(() => {
          this.viewportSyncTarget = null;
        }, 500);
      }

      this.viewport.scrollTo({
        left: nextOffset,
        behavior: instant ? 'auto' : 'smooth',
      });
    }

    updateIndicator() {
      if (!this.indicator || !this.triggers.length) {
        return;
      }

      const activeTrigger = this.triggers[this.currentIndex];
      const controls = this.querySelector('.purchase-image-switcher__controls');

      if (!activeTrigger || !controls) {
        return;
      }

      const triggerRect = activeTrigger.getBoundingClientRect();
      const controlsRect = controls.getBoundingClientRect();
      const offset = triggerRect.left - controlsRect.left;

      this.indicator.style.width = `${triggerRect.width}px`;
      this.indicator.style.transform = `translateX(${offset}px)`;
    }

    startAutoplay() {
      if (!this.autoplayEnabled || this.triggers.length < 2 || this.isPointerInside || this.isFocusInside || this.isTouchDragging) {
        return;
      }

      this.stopAutoplay();
      this.timer = window.setInterval(() => {
        const nextIndex = (this.currentIndex + 1) % this.panels.length;
        this.setActive(nextIndex);
      }, this.interval);
    }

    stopAutoplay() {
      if (!this.timer) {
        return;
      }

      window.clearInterval(this.timer);
      this.timer = null;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    pauseAutoplay(event) {
      if (event.type === 'mouseenter') {
        this.isPointerInside = true;
      }

      if (event.type === 'focusin') {
        this.isFocusInside = true;
      }

      this.stopAutoplay();
    }

    resumeAutoplay(event) {
      if (event.type === 'mouseleave') {
        this.isPointerInside = false;
      }

      if (event.type === 'focusout' && !this.contains(event.relatedTarget)) {
        this.isFocusInside = false;
      }

      this.startAutoplay();
    }
  });
}

document.addEventListener('shopify:block:select', (event) => {
  const target = event.target instanceof Element ? event.target : null;

  if (!target) {
    return;
  }

  const trigger = target.matches('[data-switcher-trigger]')
    ? target
    : target.querySelector('[data-switcher-trigger]');

  if (!trigger) {
    return;
  }

  const switcher = trigger.closest('purchase-image-switcher');

  if (!switcher || typeof switcher.setActive !== 'function') {
    return;
  }

  const index = Number(trigger.dataset.index);
  switcher.setActive(index, { focus: true });
});